import { useCallback, useEffect, useState } from 'react';
import { onIdTokenChanged } from 'firebase/auth';
import { auth } from '@actions/services/firestore/browser/firebase';
import type { UseIsAdminResult } from './types';
import canAccessOrganizationHelper from './helpers/canAccessOrganization';
import parseDeveloperOrganizationIdsFromClaims from './helpers/parseDeveloperOrganizationIdsFromClaims';

/**
 * Resolves the caller's platform principal from Firebase ID token claims:
 * - `isAdmin`: platform `roles` includes `admin`
 * - `developerOrganizationIds`: orgs where `organizations[orgId] === 'developer'`
 * - `canAccessOrganization`: true for platform admins or developers of that org
 *
 * Mirrors API principal checks (`buildPrincipal` / `canPrincipalAccessOrganization`).
 */
const useIsAdmin = (): UseIsAdminResult => {
    const [isAdmin, setIsAdmin] = useState(false);
    const [developerOrganizationIds, setDeveloperOrganizationIds] = useState<string[]>([]);
    const [isLoadingAdmin, setIsLoadingAdmin] = useState(true);

    useEffect(() => {
        const unsubscribe = onIdTokenChanged(auth, async user => {
            if (!user) {
                setIsAdmin(false);
                setDeveloperOrganizationIds([]);
                setIsLoadingAdmin(false);
                return;
            }

            try {
                const { claims } = await user.getIdTokenResult();
                const roles = claims.roles;
                const nextIsAdmin = Array.isArray(roles) && roles.includes('admin');
                setIsAdmin(nextIsAdmin);
                setDeveloperOrganizationIds(
                    nextIsAdmin
                        ? []
                        : parseDeveloperOrganizationIdsFromClaims({
                              organizationsClaim: claims.organizations,
                          })
                );
            } catch (error) {
                console.error('Error resolving auth principal:', error);
                setIsAdmin(false);
                setDeveloperOrganizationIds([]);
            } finally {
                setIsLoadingAdmin(false);
            }
        });

        return unsubscribe;
    }, []);

    const canAccessOrganization = useCallback(
        (organizationId: string) =>
            canAccessOrganizationHelper({
                isAdmin,
                developerOrganizationIds,
                organizationId,
            }),
        [isAdmin, developerOrganizationIds]
    );

    return {
        isAdmin,
        isLoadingAdmin,
        developerOrganizationIds,
        canAccessOrganization,
    };
};

export default useIsAdmin;
